import { CustomEventWithDetail, CustomEventWithoutDetail, type Html, html, on, View } from 'rune-ts';
import klass from './StudioCalendar.module.scss';
import flatpickr from 'flatpickr';
import CustomLocale = flatpickr.CustomLocale;
import { Korean } from 'flatpickr/dist/l10n/ko.js';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js';
import { classes, htmlIf } from '../../../../shared/util';
import { ButtonAction } from '../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { addDays } from 'date-fns';

export type DateViewType = 'date' | 'month';

export interface CalendarProps {
  type: 'single' | 'range';
  max_date?: Date | null;
  min_date?: Date | null;
  is_mobile?: boolean;
  start_date?: Date;
  end_date?: Date;
  enable_time?: boolean;
  initial_date?: Date;
}

export class ChangeCalendarEvent extends CustomEventWithDetail<{
  start_date: Date;
  end_date: Date;
}> {}

export class CloseCalendarEvent extends CustomEventWithoutDetail {}

export class StudioCalendar extends View<CalendarProps> {
  picker: flatpickr.Instance | undefined;
  private elementId = `date_picker_${Math.random().toString(36).substring(2, 11)}`;

  private readonly initialState = {
    view_type: 'date' as DateViewType,
    current_year: new Date().getFullYear(),
    current_month: new Date().getMonth(),
    temp_selection: null as Date | null,
    is_first_selection: true as boolean,
    start_date: this.data.start_date ?? new Date(),
    end_date: this.data.end_date ?? new Date(),
    is_mobile: this.data.is_mobile ?? false,
  };

  state = { ...this.initialState };

  override template(): Html {
    const btn_confirm_txt = ET('mps2::modal::확인');
    const btn_cancel_txt = ET('mps2::modal::취소');

    return html`
      <div
        class="${classes(klass.root, {
          [klass.single]: this.data.type === 'single',
        })}"
      >
        <div id="${this.elementId}"></div>
        <div class="${klass.calendar_btns}">
          ${htmlIf(
            new ButtonAction({
              klass: 'calendar_modal_close_btn',
              label: btn_cancel_txt,
              size: 'regular',
              submit: false,
              type: 'line',
            }),
            this.state.is_mobile,
          )}
          ${htmlIf(
            new ButtonAction({
              klass: 'calendar_modal_confirm_btn',
              label: btn_confirm_txt,
              size: 'regular',
              submit: false,
              type: 'blue',
            }),
            this.state.is_mobile,
          )}
        </div>
      </div>
    `;
  }

  override onMount() {
    setTimeout(() => {
      this.initializePicker();
    }, 0);
  }

  override onUnmount() {
    if (this.picker) {
      this.picker.destroy();
      this.picker = undefined;
    }
  }

  initializePicker(viewType?: DateViewType) {
    if (this.picker) {
      this.picker.destroy();
    }

    const target = document.getElementById(this.elementId);
    if (!target) return;

    const { min_date, max_date } = this.data;
    const type = viewType || this.state.view_type;

    if (type === 'month') {
      this.picker = this.getMonthDateSelector(target);
    } else {
      this.picker = this.getRangeDateSelector(target, min_date, max_date);

      if (this.picker && this.data.type === 'range') {
        this.picker.setDate([this.state.start_date, this.state.end_date], false);
      }
    }

    if (this.picker) {
      const displayDate = this.data.initial_date || this.state.end_date;
      this.picker.jumpToDate(displayDate);
    }
  }

  private getRangeDateSelector(target: HTMLElement, min_date?: Date | null, max_date?: Date | null) {
    const CustomKorean: CustomLocale = {
      weekdays: {
        shorthand: ['일', '월', '화', '수', '목', '금', '토'],
        longhand: ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'],
      },
      months: {
        shorthand: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
        longhand: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
      },
      rangeSeparator: ' ~ ',
      yearAriaLabel: '년',
      monthAriaLabel: '월',
      hourAriaLabel: '시',
      minuteAriaLabel: '분',
      amPM: ['오전', '오후'],
    };

    return flatpickr(target, {
      locale: CustomKorean,
      mode: this.data.type,
      inline: true,
      allowInput: false,
      monthSelectorType: 'static',
      ...(min_date && { minDate: min_date }),
      ...(max_date && { maxDate: max_date }),
      defaultDate: this.data.type === 'single' ? undefined : [this.state.start_date, this.state.end_date],
      enableTime: !!this.data.enable_time,
      minuteIncrement: 1,
      time_24hr: false,
      dateFormat: this.data.enable_time ? 'Y-m-d H:i' : 'Y-m-d',
      onChange: (selectedDates) => {
        if (this.data.type === 'single') {
          if (selectedDates?.length > 0) {
            const selectedDate = selectedDates[0];
            if (!selectedDate) return;

            this.state.start_date = new Date(selectedDate);
            this.state.end_date = new Date(selectedDate);

            // 모바일이 아닌 경우에만 이벤트 발생 및 캘린더 닫기
            if (!this.state.is_mobile) {
              this.dispatchEvent(ChangeCalendarEvent, {
                detail: {
                  start_date: this.state.start_date,
                  end_date: this.state.end_date,
                },
                bubbles: true,
              });
              this.dispatchEvent(CloseCalendarEvent, { bubbles: true });
            }
          }
          return;
        }

        if (selectedDates?.length > 0) {
          if (selectedDates.length === 1) {
            const selectedDate = selectedDates[0];

            if (this.state.is_first_selection) {
              if (selectedDate) {
                this.state.temp_selection = selectedDate;
                this.state.is_first_selection = false;

                this.state.start_date = selectedDate;
                this.state.end_date = selectedDate;
              }
            } else {
              const start = this.state.temp_selection;
              const end = selectedDate;

              if (start && end) {
                if (start > end) {
                  this.state.start_date = end;
                  this.state.end_date = start;
                } else {
                  this.state.start_date = start;
                  this.state.end_date = end;
                }

                this.state.is_first_selection = true;
                this.state.temp_selection = null;

                if (!this.state.is_mobile) {
                  this.dispatchEvent(ChangeCalendarEvent, {
                    detail: {
                      start_date: this.state.start_date,
                      end_date: this.state.end_date,
                    },
                    bubbles: true,
                  });
                  this.dispatchEvent(CloseCalendarEvent, { bubbles: true });
                }
              }
            }
          } else if (selectedDates.length > 1) {
            const start = selectedDates[0];
            const end = selectedDates[1];

            if (start && end) {
              this.state.start_date = start;
              this.state.end_date = end;
              this.state.is_first_selection = true;
              this.state.temp_selection = null;
            }

            if (!this.state.is_mobile) {
              this.dispatchEvent(ChangeCalendarEvent, {
                detail: {
                  start_date: this.state.start_date,
                  end_date: this.state.end_date,
                },
                bubbles: true,
              });
              this.dispatchEvent(CloseCalendarEvent, { bubbles: true });
            }
          }
        }
      },
      onReady: (_, __, instance) => {
        const month_el = instance.monthNav.querySelector('.flatpickr-month');

        month_el?.addEventListener('click', () => {
          this.toggleViewType('month');
        });
      },
    });
  }

  private getMonthDateSelector(target: HTMLElement) {
    const { min_date, max_date } = this.data;
    const defaultDate = this.state.end_date;

    return flatpickr(target, {
      defaultDate,
      inline: true,
      static: true,
      monthSelectorType: 'static',
      onChange: (selectedDates) => {
        const selectedDate = selectedDates[0];

        if (selectedDate) {
          const selectedMonth = selectedDate.getMonth();
          const selectedYear = selectedDate.getFullYear();

          this.state.view_type = 'date';
          this.redraw();

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (this.picker) {
                this.picker.destroy();
              }

              const datePickerElement = document.getElementById(this.elementId);
              if (!datePickerElement) return;

              this.picker = this.getRangeDateSelector(datePickerElement, min_date, max_date);

              if (this.picker) {
                if (this.state.temp_selection) {
                  this.picker.setDate(this.state.temp_selection, false);
                } else if (this.data.type === 'range') {
                  this.picker.setDate([this.state.start_date, this.state.end_date], false);
                }

                const viewDate = new Date(selectedYear, selectedMonth, 1);
                this.picker.jumpToDate(viewDate);
              }
            });
          });
        } else {
          this.state.view_type = 'date';
          this.redraw();
          requestAnimationFrame(() => {
            if (this.picker) {
              this.picker.destroy();
            }

            const datePickerElement = document.getElementById(this.elementId);
            if (!datePickerElement) return;

            this.picker = this.getRangeDateSelector(datePickerElement, min_date, max_date);

            if (this.picker) {
              if (this.state.temp_selection) {
                this.picker.setDate(this.state.temp_selection, false);
              } else if (this.data.type === 'range') {
                this.picker.setDate([this.state.start_date, this.state.end_date], false);
              }

              this.picker.jumpToDate(addDays(min_date || new Date(2020, 0, 1), 1));
            }
          });
        }
      },
      minDate: min_date ? new Date(min_date.getFullYear(), min_date.getMonth(), 1) : undefined,
      maxDate: max_date ? new Date(max_date.getFullYear(), max_date.getMonth() + 1, 0) : undefined,
      locale: Korean,
      disableMobile: true,
      plugins: [
        monthSelectPlugin({
          shorthand: true,
          dateFormat: 'Y-m',
          altFormat: 'F Y',
        }),
      ],
      onReady: (_, __, instance) => {
        const headerElement = instance.calendarContainer.querySelector('.flatpickr-month');
        if (headerElement) {
          headerElement.addEventListener('click', () => {
            this.toggleViewType('date');
          });
        }
      },
    });
  }

  private toggleViewType(viewType?: DateViewType) {
    if (viewType) {
      this.state.view_type = viewType;
    } else {
      this.state.view_type = this.state.view_type === 'date' ? 'month' : 'date';
    }

    this.redraw();

    if (this.state.view_type === 'date') {
      if (this.data.enable_time) {
        this.element().classList.add(klass.enable_time);
      }
      requestAnimationFrame(() => {
        if (this.picker) {
          this.picker.destroy();
        }

        const datePickerElement = document.getElementById(this.elementId);
        if (!datePickerElement) return;

        this.picker = this.getRangeDateSelector(datePickerElement, this.data.min_date, this.data.max_date);

        if (this.picker) {
          if (this.data.type === 'range') {
            this.picker.setDate([this.state.start_date, this.state.end_date], false);
          }
          this.picker.jumpToDate(this.state.end_date);
        }
      });
    } else {
      if (this.data.enable_time) {
        this.element().classList.remove(klass.enable_time);
      }
      requestAnimationFrame(() => {
        this.initializePicker(this.state.view_type);
      });
    }
  }

  @on('click', '.calendar_modal_close_btn')
  handleModalClose() {
    this.dispatchEvent(CloseCalendarEvent, { bubbles: true });
  }

  @on('click', '.calendar_modal_confirm_btn')
  handleModalConfirm() {
    if (this.data.is_mobile) {
      this.dispatchEvent(ChangeCalendarEvent, {
        detail: {
          start_date: this.state.start_date,
          end_date: this.state.end_date,
        },
        bubbles: true,
      });
      this.dispatchEvent(CloseCalendarEvent, { bubbles: true });
    }
  }

  public setMaxDate(date?: Date | null) {
    this.data.max_date = date;

    if (this.picker) {
      this.picker.set('maxDate', date);
      this.initializePicker(this.state.view_type);
    }
  }

  public setMinDate(date?: Date | null) {
    this.data.min_date = date;

    if (this.picker) {
      this.picker.set('minDate', date);
      this.initializePicker(this.state.view_type);
    }
  }

  public clear() {
    this.state = { ...this.initialState };

    if (this.picker) {
      this.picker.destroy();
      this.picker = undefined;
    }

    this.onMount();
  }
}
